import config from 'config';
import memoize from 'memoize-one';
import queryParserFactory from '@mulesoft/exchange-commons/src/utils/queryParserUtils';
import * as searchService from '~/services/searchService';
import * as arrayUtils from '~/utils/arrays';
import * as objectsUtils from '~/utils/objects';
import * as stringsUtils from '~/utils/strings';
import {
  ALL_ASSETS_LINK_ID,
  ALL_ORGANIZATION_LINK_ID,
  ACTIVE_ORGANIZATION_LINK_ID,
  MULESOFT_ORGANIZATION_LINK_ID,
  SHARED_WITH_ME_LINK_ID
} from '~/utils/navigation/constants';
import { SHARED_WITH_ME_KEY } from '~/utils/ang/constants';
import { getActiveOrganizationId } from '~/utils/organization';
import * as constants from './constants';

const { presetLinks } = config;
const { parseNode } = queryParserFactory();

const getQueryByFilters = memoize((filters, multiFilters) => {
  const query = {
    search: filters.search ? filters.search : undefined
  };

  if (filters.sharedWithMe.length) {
    query.sharedWithMe = filters.sharedWithMe;
  }

  multiFilters.forEach((key) => {
    const camelizedKey = stringsUtils.camelizeUnderscore(key);

    query[key] = filters[camelizedKey].length
      ? filters[camelizedKey]
      : undefined;
  });

  return Object.keys(query).reduce((accum, key) => {
    if (query[key] !== undefined) {
      return { ...accum, [key]: query[key] };
    }

    return accum;
  }, {});
});

const getFacetTerm = (facet) => {
  if (facet === 'customer-metadata.CustomerMetadataNode.categories') {
    return '_aggr_category_t';
  }

  return '_aggr_tag_t';
};

function getTypes(angState, typesList) {
  const angTypes = angState.filters[constants.TYPE_KEY];

  if (!angTypes) {
    return [];
  }

  return Object.keys(typesList).filter((typeKey) => {
    if (typeKey === 'default') {
      return false;
    }

    const type = typesList[typeKey];

    return angTypes.some((selectedType) => selectedType === type.angType);
  });
}

const getANGTypes = memoize((types, typesList) =>
  types
    .map((queryType) => typesList[queryType] && typesList[queryType].angType)
    .filter((angType) => angType)
    .reduce((acum, current) => {
      if (acum.indexOf(current) > -1) {
        return acum;
      }

      return [...acum, current];
    }, [])
);

export const parseQuery = (query) => {
  const parsedQuery = parseNode(query);

  return {
    search: parsedQuery.query || '',
    tag: parsedQuery.tags
      .filter((tag) => tag.type === 'label')
      .map((tag) => tag.value),
    category: parsedQuery.tags
      .filter((tag) => tag.type === 'category')
      .map((category) => `${category.key}:${category.value}`)
  };
};

export const buildFiltersFromPreset = ({ filters: presetFilters }, profile) => {
  const { categories } = presetFilters || {};
  const category = (categories || []).map(
    ({ tagKey, value }) => `${tagKey}:${value}`
  );

  return {
    ...(profile ? { organizationId: [config.mulesoft.id] } : {}),
    ...(category.length ? { category } : {})
  };
};

export const filtersByShowAuthenticated = (
  show,
  profile,
  isQueryEmpty,
  rootOrganizations
) => {
  const allOrganizationFilters = {
    organizationId: rootOrganizations || []
  };

  if (isQueryEmpty) {
    return allOrganizationFilters;
  }

  switch (show) {
    case ALL_ORGANIZATION_LINK_ID:
      return allOrganizationFilters;

    case ACTIVE_ORGANIZATION_LINK_ID:
      return { organizationId: [getActiveOrganizationId(profile)] };

    case MULESOFT_ORGANIZATION_LINK_ID:
      return { organizationId: [config.mulesoft.id] };

    case SHARED_WITH_ME_LINK_ID:
      return { sharedWithMe: [true] };

    default: // (ALL_ASSETS_LINK_ID, undefined)
      return {};
  }
};

const filtersByShowUnauthenticated = (show) => {
  const presetLink = show && presetLinks.find(({ id }) => id === show);

  return presetLink ? buildFiltersFromPreset(presetLink) : {};
};

const transformMatchingGlobalSections = (query) => {
  const match = presetLinks.find((presetLink) => {
    const presetFilters = buildFiltersFromPreset(presetLink);

    return arrayUtils.shallowEquals(query.category, presetFilters.category);
  });

  return match
    ? {
        ...objectsUtils.omit(query, 'category'),
        show: match.id
      }
    : query;
};

const transformMatchingPrivateSections = (
  query,
  profile,
  rootOrganizations
) => {
  const { organizationId, [SHARED_WITH_ME_KEY]: sharedWithMe } = query;

  if (sharedWithMe) {
    return {
      ...objectsUtils.omit(query, SHARED_WITH_ME_KEY),
      show: SHARED_WITH_ME_LINK_ID
    };
  }

  const matches = (organizationIds) =>
    arrayUtils.shallowEquals(organizationId, organizationIds, true);
  const defaultQuery = { ...query, show: ALL_ASSETS_LINK_ID };
  let show;

  if (!organizationId) {
    return defaultQuery;
  }

  if (matches([getActiveOrganizationId(profile)])) {
    show = ACTIVE_ORGANIZATION_LINK_ID;
  } else if (matches(rootOrganizations || [])) {
    show = ALL_ORGANIZATION_LINK_ID;
  } else if (matches([config.mulesoft.id])) {
    show = MULESOFT_ORGANIZATION_LINK_ID;
  }

  return show
    ? { ...objectsUtils.omit(query, 'organizationId'), show }
    : defaultQuery;
};

export default class ANGFilters {
  static multiFilters = [
    'type',
    'category',
    'organizationId',
    'tag',
    'status',
    'updated_at'
  ];

  static hasQuery = memoize(
    (query = {}) =>
      !!query.search ||
      ANGFilters.multiFilters.some(
        (key) => arrayUtils.convertToArray(query[key]).length > 0
      )
  );

  static getBoostConfiguration = (query) =>
    arrayUtils.convertToArray(query[constants.BOOST_KEY]);

  static buildFromQuery = memoize(
    (
      query = {},
      assetTypesConfiguration,
      profile,
      isANGSidebarConsistencyEnabled,
      rootOrganizations = []
    ) => {
      const isQueryEmpty = Object.keys(query).length === 0;
      const parsedQuery = parseQuery(query.search);
      const filters = {
        search: parsedQuery.search,
        hasQuery: ANGFilters.hasQuery(query),
        sharedWithMe: arrayUtils.convertToArray(query.sharedWithMe)
      };
      let showFilters = {};

      ANGFilters.multiFilters.forEach((key) => {
        filters[stringsUtils.camelizeUnderscore(key)] = arrayUtils
          .convertToArray(query[key])
          .concat(parsedQuery[key] || []);
      });

      if (!profile) {
        showFilters = filtersByShowUnauthenticated(query.show);
      } else if (isANGSidebarConsistencyEnabled) {
        showFilters = filtersByShowAuthenticated(
          query.show,
          profile,
          isQueryEmpty,
          rootOrganizations
        );
      }

      return new ANGFilters(
        { ...filters, ...showFilters },
        assetTypesConfiguration
      );
    }
  );

  static buildUpdateFilter = memoize(
    (
      query,
      assetTypesConfiguration,
      context,
      onFilterUpdate,
      organizationId
    ) => {
      let lastTerm = '';

      return async (term, facet) => {
        if (term !== lastTerm) {
          const queryWithTerm = {
            ...query,
            [getFacetTerm(facet)]: term
          };
          const facets = await searchService.getFacets({
            context,
            query: queryWithTerm,
            assetTypesConfiguration,
            facet,
            organizationId
          });

          lastTerm = term;
          onFilterUpdate(facets);
        }
      };
    }
  );

  static buildFromANGState = memoize((angState, typesList) => {
    const filters = {
      search: angState.query || '',
      hasQuery: true,
      type: getTypes(angState, typesList),
      updatedAt: angState.filters[constants.UPDATED_AT_KEY] || [],
      category: angState.filters[constants.CATEGORIES_KEY] || [],
      organizationId: angState.filters[constants.ORGANIZATION_KEY] || [],
      status: angState.filters[constants.STATUS_KEY] || [],
      tag: angState.filters[constants.TAGS_KEY] || [],
      sharedWithMe: angState.filters[constants.SHARED_WITH_ME_KEY] || []
    };

    return new ANGFilters(filters, typesList);
  });

  static getAsANGState = memoize((filters, typesList) => {
    const angState = {
      query: filters.search,
      filters: {},
      customFilters: {}
    };

    angState.filters[constants.TYPE_KEY] = getANGTypes(filters.type, typesList);
    angState.filters[constants.CATEGORIES_KEY] = filters.category;
    angState.filters[constants.ORGANIZATION_KEY] = filters.organizationId;
    angState.filters[constants.TAGS_KEY] = filters.tag;
    angState.filters[constants.UPDATED_AT_KEY] = filters.updatedAt;
    angState.filters[constants.STATUS_KEY] = filters.status;
    angState.filters[constants.SHARED_WITH_ME_KEY] = filters.sharedWithMe;

    return angState;
  });

  constructor(filters, types = {}) {
    this.filters = filters;
    this.types = types;
  }

  getFilters = () => this.filters;

  hasQuery = () => this.filters.hasQuery;

  getAsANGState = () => {
    const { filters, types } = this;

    return ANGFilters.getAsANGState(filters, types);
  };

  getAsQuery = () => getQueryByFilters(this.filters, ANGFilters.multiFilters);

  getNewQuery = (
    show,
    profile,
    isANGSidebarConsistencyEnabled,
    rootOrganizations
  ) => {
    const query = this.getAsQuery();

    if (!profile) {
      return transformMatchingGlobalSections(query);
    }

    return isANGSidebarConsistencyEnabled
      ? transformMatchingPrivateSections(query, profile, rootOrganizations)
      : query;
  };
}
